import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChildren,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';
import { VerwendungReiter, VerwendungStatus } from '../../models/verwendung.model';
import { KonfigPillComponent } from '../konfig-pill/konfig-pill.component';
import { DetailsHistorieStatusComponent } from '../details-historie-card/details-historie-status/details-historie-status.component';
import { SectionTitleComponent } from '../section-title/section-title.component';

const MAX_ZEILEN = 200;
const SUCHE_AB = 7;

@Component({
  selector: 'app-verwendung-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    RouterLink,
    TranslatePipe,
    DetailsHistorieStatusComponent,
    KonfigPillComponent,
    SectionTitleComponent,
  ],
  templateUrl: './verwendung-card.component.html',
  styleUrl: './verwendung-card.component.scss',
})
export class VerwendungCardComponent {
  private readonly translate = inject(TranslateService);

  readonly testId = input.required<string>();
  readonly tabs = input.required<VerwendungReiter[]>();
  readonly subtitle = input<string>('');
  readonly status = input<VerwendungStatus>('bereit');
  readonly fehlerText = input<string | null>(null);
  readonly erneutVersuchen = output<void>();

  protected readonly maxZeilen = MAX_ZEILEN;
  private readonly buttons = viewChildren<ElementRef<HTMLButtonElement>>('reiterButton');
  private readonly sprache = toSignal(this.translate.onLangChange.pipe(map((event) => event.lang)), {
    initialValue: this.translate.getCurrentLang(),
  });

  private readonly gewaehlt = signal<string | null>(null);
  protected readonly suchtext = signal('');

  protected readonly aktiv = computed(() => {
    const tabs = this.tabs();
    return tabs.find((tab) => tab.id === this.gewaehlt()) ?? tabs[0];
  });
  protected readonly zeigeSuche = computed(
    () => this.status() === 'bereit' && this.aktiv().eintraege.length >= SUCHE_AB,
  );
  protected readonly aktiveSuche = computed(() => (this.zeigeSuche() ? this.suchtext().trim().toLowerCase() : ''));
  protected readonly gefiltert = computed(() => {
    const eintraege = this.aktiv().eintraege;
    const suche = this.aktiveSuche();
    if (!suche) return eintraege;
    return eintraege.filter((e) => `${e.key ?? ''} ${e.titel}`.toLowerCase().includes(suche));
  });
  protected readonly sichtbar = computed(() => this.gefiltert().slice(0, MAX_ZEILEN));

  protected zahl(wert: number): string {
    return new Intl.NumberFormat(this.sprache() || 'de').format(wert);
  }

  protected waehle(id: string): void {
    if (id === this.aktiv().id) return;
    this.gewaehlt.set(id);
    this.suchtext.set('');
  }

  protected onSuche(event: Event): void {
    this.suchtext.set((event.target as HTMLInputElement).value);
  }

  protected onKeydown(event: KeyboardEvent, index: number): void {
    const ziel = this.zielIndex(event.key, index);
    if (ziel === null) return;
    event.preventDefault();
    this.waehle(this.tabs()[ziel].id);
    this.buttons()[ziel].nativeElement.focus();
  }

  protected tabElementId(id: string): string {
    return `${this.testId()}-tab-${id}`;
  }

  private zielIndex(key: string, index: number): number | null {
    const anzahl = this.tabs().length;
    switch (key) {
      case 'ArrowRight':
        return (index + 1) % anzahl;
      case 'ArrowLeft':
        return (index - 1 + anzahl) % anzahl;
      case 'Home':
        return 0;
      case 'End':
        return anzahl - 1;
      default:
        return null;
    }
  }
}
